<script>
	import Vue from 'vue'
	import axios from 'axios'
	import URI from 'urijs';
	import { ConsoleLogger } from '@/console-logger'
	import clone from 'clone';
	import RequestFactory from './requestFactory';
	import Mixins from './mixins';

	export default {
		name: 'api-client-core',
		data() {
			return {
				http: null,
				lastResponse: null,

				baseUrl: null,

				logMessages: [],
				logger: new ConsoleLogger('ApiClient', 'background: #0000aa; color: white'),

				options: {},

				mixinMethods: {}
			}
		},
		created() {
			if (!Vue.prototype.$apiClient) {
				Object.defineProperty(Vue.prototype, '$apiClient', {value: this})
			}

			const baseUrl = process.env.VUE_APP_API_URL;

			this.http = axios.create({
				timeout: 60000,
				baseURL: baseUrl,
			});

			this.baseUrl = baseUrl;

			this.http.interceptors.response.use(undefined, (err) => {
				const count = (err.config || {}).retryCount;

				if (count > 0) {
					return axios({ ...err.config, retryCount: count - 1 });
				}

				throw err;
			});

			for (let Mixin of Mixins) {
				for (let method in Mixin.methods) {
					this.mixinMethods[method] = Mixin.methods[method];
				}
			}

			this.logMessage(`Assigned to Vue.$apiClient (base url: ${baseUrl})`)
		},
		methods: {
			noErrorHandler() {
				return this.requestFactory({ noErrorHandler: true });
			},
			raw() {
				return this.requestFactory({ raw: true });
			},
			requestFactory(options = {}) {
				let requestFactory = new RequestFactory(this, options);

				let mixinMethods = this.mixinMethods;

				return new Proxy(requestFactory, {
					get: (target, prop, receiver) => {
						if (mixinMethods.hasOwnProperty(prop)) {
							return mixinMethods[prop].bind(requestFactory);
						}
					}
				});
			},

			setOptions(options) {
				for (let key in options) {
					Vue.set(this.options, key, options[key]);
				}
			},

			getHttp() {
				return this.http;
			},
			addGetParams(url, getParams) {
				return URI(url).query(getParams).href();
			},
			getUrl(relativeUrl) {
				return this.baseUrl.replace(/\/$/, '') + '/' + relativeUrl.replace(/^\//, '');
			},

			get(endPoint, getParams = null) {
				return this.request('get', endPoint, getParams, null);
			},
			post(endPoint, data = null, getParams = null) {
				return this.request('post', endPoint, getParams, data);
			},
			put(endPoint, data = null, getParams = null) {
				return this.request('put', endPoint, getParams, data);
			},
			delete(endPoint, data = null, getParams = null) {
				return this.request('delete', endPoint, getParams, data);
			},
			request(type, endPoint, getParams, data, options) {
				let typePrint = type.toUpperCase();

				let raw = false;
				let noErrorHandler = false;

				if (getParams) {
					endPoint = this.addGetParams(endPoint, getParams);
				}

				if (options) {
					if (options.hasOwnProperty('raw')) {
						raw = options.raw;
					}

					if (options.hasOwnProperty('noErrorHandler')) {
						noErrorHandler = options.noErrorHandler;
					}
				}

				return this.http[type](endPoint, data).then((response) => {
					this.lastResponse = response;

					if (response && response.hasOwnProperty('data')) {
						let data = response.data;
						let httpCode = response.data.hasOwnProperty('statusCode') ? response.data.statusCode : response.status;

						if (httpCode === 200 && (raw || response.data.success)) {
							if (raw) {
								return response.data;
							} else if (response.data.hasOwnProperty('data')) {
								return response.data.data;
							} else {
								return null;
							}
						} else {
							let error = 'Unknown';

							if (data.hasOwnProperty('error')) {
								if (typeof(data.error) === 'string') {
									error = data.error;
								} else if (typeof(data.error) === 'object' && data.error.hasOwnProperty('message')) {
									error = data.error.message;
								}
							}

							return Promise.reject({
								response: response,
								error: error,
								status: httpCode
							});
						}
					} else {
						return Promise.reject('Could not find any data in response');
					}
				}).catch((error) => {
					if (error.response) {
						this.lastResponse = error.response;
					} else {
						this.lastResponse = null;
					}

					let statusCode = 500;

					if (typeof(error) === 'object' && error.status) {
						statusCode = error.status;
					} else if (this.lastResponse && this.lastResponse.status) {
						statusCode = this.lastResponse.status;
					}

					if (!noErrorHandler && this.errorHandler) {
						let errorString;

						let errorResponse = {
							response: {
								status: statusCode,
								raw: this.lastResponse,
							},
							request: {
								method: typePrint,
								url: endPoint,
							},
							error: {
								raw: error,
							}
						};

						if (error.error) {
							errorString = `[${errorResponse.response.status}] An error happened when calling API url ${errorResponse.request.method} ${errorResponse.request.url}: ${error.error}`;
						} else if (error.response && error.response.status) {
							errorString = `[${errorResponse.response.status}] Received error code when calling API url ${errorResponse.request.method} ${errorResponse.request.url}`;
						} else {
							errorString = `An unknown error happened when calling API url ${errorResponse.request.method} ${errorResponse.request.url}`;
						}

						this.errorHandler(errorString);
					}

					throw error
				});
			},

			// Logger
			clearLog () {
				console.log('Clearing log!')

				this.logMessages = []
			},
			logMessage (message) {
				this.logger.logMessage(message)

				this.logMessages.push(message)
			},
		},
		render: function (h) {
			return h() // avoid warning message
		}
	}
</script>
